import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { RefreshCw, Save, ShieldCheck } from 'lucide-react';
import { API_URL } from '../config/api';
import { moduleCatalog, permissionRoles, reportPermissionItems } from '../config/menuCatalog';

const roles = permissionRoles;
const permissionRoleSet = new Set(roles.map((role) => role.key));
const allowedPermissionRoles = (item) => (item.strictRoles || item.roles || []).filter((role) => permissionRoleSet.has(role));
const submenuChildren = (item) => (Array.isArray(item?.children) ? item.children : []);
const flattenPermissionSubmenus = (menuItem, subItems = [], parentRoles = menuItem.roles || [], path = []) => (
  subItems.flatMap((subItem) => {
    const rolesForItem = allowedPermissionRoles({ ...subItem, roles: subItem.roles || parentRoles });
    const currentPath = [...path, subItem.title];
    const currentItem = rolesForItem.length > 0
      ? [{
          key: `${menuItem.key}:${subItem.key}`,
          title: subItem.title,
          group: `${menuItem.title}${currentPath.length > 1 ? ` / ${currentPath.slice(0, -1).join(' / ')}` : ''}`,
          roles: rolesForItem
        }]
      : [];
    return [
      ...currentItem,
      ...flattenPermissionSubmenus(menuItem, submenuChildren(subItem), subItem.roles || parentRoles, currentPath)
    ];
  })
);
const menuItems = moduleCatalog
  .filter((item) => item.key !== 'dashboard' && allowedPermissionRoles(item).length > 0)
  .map((item) => ({
    key: item.key,
    title: item.title,
    group: item.group,
    roles: allowedPermissionRoles(item),
    subItems: item.subItems || []
  }));
const submenuItems = menuItems.flatMap((item) => (
  flattenPermissionSubmenus(item, item.subItems || [], item.roles)
));
const reportItems = reportPermissionItems;

const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const roleLabel = (role) => roles.find((item) => item.key === role)?.label || role;
const makeKey = (type, key) => `${type}:${key}`;
const normalizeHiddenByRole = (value = {}) => roles.reduce((acc, role) => {
  acc[role.key] = Array.from(new Set(Array.isArray(value[role.key]) ? value[role.key] : []));
  return acc;
}, {});
const card = {
  background: 'white',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  padding: '16px',
  boxShadow: '0 1px 5px rgba(15, 23, 42, 0.06)'
};

export default function MenuPermissions({ permissionView = '' }) {
  const [admins, setAdmins] = useState([]);
  const [selectedAdminId, setSelectedAdminId] = useState('');
  const [activeRole, setActiveRole] = useState('admin');
  const [hiddenByRole, setHiddenByRole] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [message, setMessage] = useState('');
  const autoSaveTimerRef = useRef(null);
  const menuSectionRef = useRef(null);
  const submenuSectionRef = useRef(null);
  const reportSectionRef = useRef(null);

  const loadPermissions = useCallback(async (adminId = '') => {
    window.clearTimeout(autoSaveTimerRef.current);
    setLoading(true);
    setMessage('');
    try {
      const params = adminId ? { admin_id: adminId } : {};
      const res = await axios.get(`${API_URL}/api/workspace/menu-permissions`, { headers: getHeaders(), params });
      const nextAdmins = res.data?.admins || [];
      const nextAdminId = String(res.data?.selected_admin_id || adminId || nextAdmins[0]?.id || '');
      setAdmins(nextAdmins);
      setSelectedAdminId(nextAdminId);
      setHiddenByRole(normalizeHiddenByRole(res.data?.permissions?.hidden_by_role || {}));
      setDirty(false);
    } catch (err) {
      setMessage(err.response?.data?.message || 'Could not load menu permissions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPermissions(''); }, [loadPermissions]);

  const selectedAdmin = admins.find((admin) => String(admin.id) === String(selectedAdminId));
  const hiddenSet = useMemo(() => new Set(hiddenByRole[activeRole] || []), [activeRole, hiddenByRole]);
  const visibleMenuItems = menuItems.filter((item) => item.roles.includes(activeRole));
  const visibleSubmenuItems = submenuItems.filter((item) => item.roles.includes(activeRole));
  const visibleReportItems = reportItems.filter((item) => item.roles.includes(activeRole));

  const updateHiddenByRole = (updater) => {
    setHiddenByRole((current) => normalizeHiddenByRole(updater(normalizeHiddenByRole(current))));
    setDirty(true);
    setMessage('Changes pending. Applying...');
  };

  const setItemVisible = (type, key, visible) => {
    const itemKey = makeKey(type, key);
    updateHiddenByRole((current) => {
      const currentKeys = new Set(current[activeRole] || []);
      if (visible) currentKeys.delete(itemKey);
      else currentKeys.add(itemKey);
      return { ...current, [activeRole]: Array.from(currentKeys) };
    });
  };

  const setSectionVisible = (items, type, visible) => {
    updateHiddenByRole((current) => {
      const currentKeys = new Set(current[activeRole] || []);
      items.forEach((item) => {
        const itemKey = makeKey(type, item.key);
        if (visible) currentKeys.delete(itemKey);
        else currentKeys.add(itemKey);
      });
      return { ...current, [activeRole]: Array.from(currentKeys) };
    });
  };

  const savePermissions = useCallback(async (options = {}) => {
    if (!selectedAdminId) {
      setMessage('Please select an admin');
      return false;
    }
    const adminId = String(selectedAdminId);
    const payload = normalizeHiddenByRole(hiddenByRole);
    setSaving(true);
    if (!options.silent) setMessage('');
    try {
      const res = await axios.put(
        `${API_URL}/api/workspace/menu-permissions/${adminId}`,
        { admin_id: adminId, hidden_by_role: payload },
        { headers: getHeaders() }
      );
      const verifyRes = await axios.get(`${API_URL}/api/workspace/menu-permissions`, {
        headers: getHeaders(),
        params: { admin_id: adminId }
      });
      setHiddenByRole(normalizeHiddenByRole(verifyRes.data?.permissions?.hidden_by_role || res.data?.permissions?.hidden_by_role || payload));
      setDirty(false);
      setMessage(`Menu permissions applied for ${selectedAdmin?.name || 'Admin'}`);
      return true;
    } catch (err) {
      setMessage(err.response?.data?.message || 'Could not save menu permissions');
      return false;
    } finally {
      setSaving(false);
    }
  }, [hiddenByRole, selectedAdmin?.name, selectedAdminId]);

  useEffect(() => {
    window.clearTimeout(autoSaveTimerRef.current);
    if (!dirty || !selectedAdminId || loading || saving) return undefined;
    autoSaveTimerRef.current = window.setTimeout(() => {
      savePermissions({ silent: true });
    }, 700);
    return () => window.clearTimeout(autoSaveTimerRef.current);
  }, [dirty, loading, savePermissions, saving, selectedAdminId]);

  useEffect(() => () => window.clearTimeout(autoSaveTimerRef.current), []);

  useEffect(() => {
    const targetMap = {
      'menu-permission-menu': menuSectionRef,
      'menu-permission-submenu': submenuSectionRef,
      'menu-permission-report': reportSectionRef
    };
    const target = targetMap[permissionView];
    if (!target?.current) return;
    target.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [permissionView]);

  const renderSwitch = (type, item) => {
    const visible = !hiddenSet.has(makeKey(type, item.key));
    return (
      <label key={item.key} style={{ border: `1px solid ${visible ? '#bbf7d0' : '#fecaca'}`, background: visible ? '#f0fdf4' : '#fef2f2', borderRadius: '8px', padding: '11px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
        <span>
          <strong style={{ display: 'block', color: '#0f172a', fontSize: '13px' }}>{item.title}</strong>
          {item.group && <small style={{ color: '#64748b' }}>{item.group}</small>}
        </span>
        <input type="checkbox" checked={visible} onChange={(event) => setItemVisible(type, item.key, event.target.checked)} />
      </label>
    );
  };

  return (
    <div className="fixed-panel-page tight" style={{ color: '#111827' }}>
      <div className="fixed-panel-header">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '18px' }}>
        <div>
          <h2 style={{ margin: 0, color: '#1e3a5f', display: 'flex', alignItems: 'center', gap: '8px' }}><ShieldCheck size={22} /> Menu Permissions</h2>
          <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: '13px' }}>Show or hide admin portal menus, menu sub-options, and report sub-options by role.</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button type="button" onClick={() => loadPermissions(selectedAdminId)} disabled={loading} style={{ border: 'none', borderRadius: '8px', padding: '10px 14px', display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#e0f2fe', color: '#0369a1', fontWeight: 800, cursor: loading ? 'wait' : 'pointer' }}><RefreshCw size={16} /> Refresh</button>
          <button type="button" onClick={() => savePermissions()} disabled={!selectedAdminId || saving} style={{ border: 'none', borderRadius: '8px', padding: '10px 14px', display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#0f766e', color: 'white', fontWeight: 800, cursor: saving ? 'wait' : 'pointer' }}><Save size={16} /> {saving ? 'Applying...' : dirty ? 'Apply Now' : 'Applied'}</button>
        </div>
      </div>

      {message && <div style={{ ...card, marginBottom: '16px', background: '#eff6ff', color: '#1d4ed8', borderColor: '#bfdbfe' }}>{message}</div>}

      <section style={{ ...card, marginBottom: '16px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 360px) 1fr', gap: '14px', alignItems: 'end' }}>
          <label>
            <span style={{ display: 'block', fontSize: '12px', color: '#374151', fontWeight: 800, marginBottom: '6px' }}>Portal / Admin</span>
            <select value={selectedAdminId} onChange={(event) => loadPermissions(event.target.value)} style={{ width: '100%', padding: '11px', borderRadius: '8px', border: '1px solid #d1d5db', color: '#111827', fontWeight: 700 }}>
              <option value="">Select admin</option>
              {admins.map((admin) => <option key={admin.id} value={admin.id}>{admin.name} - {roleLabel(admin.role)}{admin.mobile ? ` - ${admin.mobile}` : ''}</option>)}
            </select>
          </label>
          <div style={{ color: '#64748b', fontSize: '13px' }}>
            {selectedAdmin ? `Editing menu controls for roles under ${selectedAdmin.name}.` : 'Please select an admin.'}
          </div>
        </div>
      </section>

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
        {roles.map((role) => (
          <button key={role.key} type="button" onClick={() => setActiveRole(role.key)} style={{ border: `1px solid ${activeRole === role.key ? '#1e3a5f' : '#dbe3ef'}`, borderRadius: '999px', padding: '9px 13px', cursor: 'pointer', background: activeRole === role.key ? '#1e3a5f' : 'white', color: activeRole === role.key ? 'white' : '#334155', fontSize: '13px', fontWeight: 800 }}>
            {role.label}
          </button>
        ))}
      </div>
      </div>

      <div className="fixed-panel-body">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px' }}>
        <section style={card} ref={menuSectionRef}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
            <h3 style={{ margin: 0, color: '#1e3a5f', fontSize: '16px' }}>{roleLabel(activeRole)} Menu</h3>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button type="button" onClick={() => setSectionVisible(visibleMenuItems, 'module', true)} style={{ border: 'none', borderRadius: '7px', padding: '7px 10px', background: '#dcfce7', color: '#15803d', cursor: 'pointer', fontWeight: 800 }}>Show All</button>
              <button type="button" onClick={() => setSectionVisible(visibleMenuItems, 'module', false)} style={{ border: 'none', borderRadius: '7px', padding: '7px 10px', background: '#fee2e2', color: '#b91c1c', cursor: 'pointer', fontWeight: 800 }}>Hide All</button>
            </div>
          </div>
          <div style={{ display: 'grid', gap: '10px' }}>{visibleMenuItems.map((item) => renderSwitch('module', item))}</div>
        </section>

        <section style={card} ref={submenuSectionRef}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
            <h3 style={{ margin: 0, color: '#1e3a5f', fontSize: '16px' }}>Menu Sub Options</h3>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button type="button" onClick={() => setSectionVisible(visibleSubmenuItems, 'submenu', true)} style={{ border: 'none', borderRadius: '7px', padding: '7px 10px', background: '#dcfce7', color: '#15803d', cursor: 'pointer', fontWeight: 800 }}>Show All</button>
              <button type="button" onClick={() => setSectionVisible(visibleSubmenuItems, 'submenu', false)} style={{ border: 'none', borderRadius: '7px', padding: '7px 10px', background: '#fee2e2', color: '#b91c1c', cursor: 'pointer', fontWeight: 800 }}>Hide All</button>
            </div>
          </div>
          <div style={{ display: 'grid', gap: '10px' }}>{visibleSubmenuItems.map((item) => renderSwitch('submenu', item))}</div>
        </section>

        <section style={card} ref={reportSectionRef}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
            <h3 style={{ margin: 0, color: '#1e3a5f', fontSize: '16px' }}>Report Sub Options</h3>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button type="button" onClick={() => setSectionVisible(visibleReportItems, 'report', true)} style={{ border: 'none', borderRadius: '7px', padding: '7px 10px', background: '#dcfce7', color: '#15803d', cursor: 'pointer', fontWeight: 800 }}>Show All</button>
              <button type="button" onClick={() => setSectionVisible(visibleReportItems, 'report', false)} style={{ border: 'none', borderRadius: '7px', padding: '7px 10px', background: '#fee2e2', color: '#b91c1c', cursor: 'pointer', fontWeight: 800 }}>Hide All</button>
            </div>
          </div>
          <div style={{ display: 'grid', gap: '10px' }}>{visibleReportItems.map((item) => renderSwitch('report', item))}</div>
        </section>
      </div>
      </div>
    </div>
  );
}
